<script setup lang="ts">
import { useId } from "vue";
import type { z } from "zod";
import { Input } from ".";
import { Label } from "@/components/ui/label";
import { useOptionalField } from "@/lib/validation";

export interface TextfieldPlusProps {
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Placeholder for the input
   */
  placeholder?: string;
  /**
   * ID of the input. If not provided, a unique ID will be generated.
   */
  id?: string;
  disabled?: boolean;
  type?: string;
  required?: boolean;

  /**
   * VeeValidate field path (e.g. `url` or `params[0].name`). When set, the input registers itself with the
   * surrounding `useForm()` and shows that field's validation error. A v-model stays in sync with the form value.
   * Whether it is set is decided at setup; the path itself may change (e.g. indexed rows).
   */
  name?: string;
  /**
   * Field-level Zod schema. Makes the `name`d field standalone: it validates on its own and does NOT join a
   * surrounding `useForm()`. For fields that belong to a form, put the rules into the form's schema instead.
   */
  schema?: z.ZodType;

  /**
   * HTML Attributes/props that will be forwarded to the label element
   */
  labelProps?: Record<string, unknown>;
  /**
   * HTML Attributes/props that will be forwarded to the input element
   */
  inputProps?: Record<string, unknown>;
  /**
   * HTML Attributes/props that will be forwarded to error message/hint element
   */
  hintProps?: Record<string, unknown>;
}

const modelValue = defineModel<string | number>();
const props = withDefaults(defineProps<TextfieldPlusProps>(), {
  type: "text",
});

const generatedId = useId();
const inputId = props.id || generatedId;

const { value, errorMessage, onBlur } = useOptionalField(() => props.name, modelValue, props.schema);
</script>

<template>
  <div :id="`textfield-plus-${inputId}`">
    <Label
      v-if="label"
      :for="inputId"
      class="mb-1"
      v-bind="labelProps"
    >
      {{ label }}
      <span v-if="required" class="text-destructive" aria-hidden="true">*</span>
    </Label>

    <Input
      :model-value="value ?? ''"
      :id="inputId"
      :name
      :type
      :placeholder
      :disabled
      :required
      :aria-required="!!required"
      :aria-invalid="!!errorMessage"
      v-bind="inputProps"
      @update:model-value="value = $event"
      @blur="onBlur"
    />

    <slot name="hint" :invalid="!!errorMessage" :message="errorMessage ?? null" :props="hintProps">
      <span
        v-if="errorMessage"
        v-bind="hintProps"
        class="text-destructive text-sm mt-1 block"
      >
        {{ errorMessage }}
      </span>
    </slot>
  </div>
</template>
